import { formatNumber } from "accounting"
import { Avatar, Badge, Dropdown, Flex, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../Components/Button"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import {
  alertsColumnIconMap,
  dateFormat,
  paymentProcessorMap,
  serviceMap,
  statusTextMap,
  tagColorMap,
  tagMap
} from "../../../../Utils/utils"

dayjs.extend(utc)

const { Text, Paragraph } = Typography

dayjs.extend(utc)

// Receipt status palette (§13.7 + Not Enrolled). Kept local to the columns file
// because tagMap/tagColorMap in Utils already assign different colours to
// `needs_help`/`unenrolled`/`failed` for other features.
// Every chip is the same unfilled tag as Status and Programs (CHA1-2727), so Not Enrolled
// tells itself from the solid gray Unenrolled by an outlined dot, a ring of its colour
// (RCAD-10-02, CHA1-4354).
const RECEIPT_STATUS_META = {
  active: { label: "Active", color: "#34C759" },
  pending: { label: "Pending", color: "#661AC6" },
  needs_help: { label: "Needs Help", color: "#FF9F0A" },
  unenrolled: { label: "Unenrolled", color: "#8C8C8C" },
  failed: { label: "Failed", color: "#EF4243" },
  not_enrolled: { label: "Not Enrolled", color: "#8C8C8C", outlined: true }
}

const PROGRAM_LABELS = { cc: "CC", oi: "OI" }

export const getColumns = ({ onSelectRow }) => ({
  status: {
    key: "status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Status",
        sorter: true,
        icon: alertsColumnIconMap["status"],
        stateData: state,
        key: "status"
      }),
    dataIndex: "status",
    fixed: "left",
    width: 120,
    render: (_) => {
      return (
        <Tag
          color={tagMap[_?.toLowerCase()] || "default"}
          key={_}
          style={{ margin: 0 }}
          className={"simple large"}
        >
          <Space>
            <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
            <Text>{statusTextMap[_] || _}</Text>
          </Space>
        </Tag>
      )
    }
  },
  steps_finished: {
    key: "steps_finished",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Steps finished",
        sorter: true,
        icon: alertsColumnIconMap["steps_finished"],
        stateData: state,
        key: "steps_finished"
      }),
    dataIndex: "steps_finished",
    width: 170
  },
  mode: {
    key: "mode",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Mode",
        sorter: false,
        icon: alertsColumnIconMap["mode"],
        stateData: state,
        key: "mode"
      }),
    dataIndex: "mode",
    render: (_) => _,
    width: 160
  },
  user_full_name: {
    key: "user_full_name",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "User full name",
        sorter: true,
        icon: alertsColumnIconMap["user_full_name"],
        stateData: state,
        key: "user_full_name"
      }),
    dataIndex: "user_full_name",
    minWidth: 150,
    render: (_, record) => (
      <Flex gap={4}>
        <Text>{_}</Text>
        {record?.count_of_business_account ? (
          <Text>({record?.count_of_business_account})</Text>
        ) : null}
      </Flex>
    )
  },
  user_email: {
    key: "user_email",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "User email",
        sorter: true,
        icon: alertsColumnIconMap["user_email"],
        stateData: state,
        key: "user_email"
      }),
    dataIndex: "user_email"
    //width: 185,
  },
  alerts_count: {
    key: "alerts_count",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Alerts sent",
        sorter: true,
        icon: alertsColumnIconMap["alerts_count"],
        stateData: state,
        key: "alerts_count"
      }),
    dataIndex: "alerts_count",
    width: 150,
    render: (_, record) => (
      <ButtonComponent
        type={"link"}
        disabled={record?.demo_mode}
        onClick={() => {
          if (!record?.demo_mode) onSelectRow(record, "alerts_count")
        }}
      >
        {_}
      </ButtonComponent>
    )
  },
  paid_alerts_count: {
    key: "paid_alerts_count",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Paid Alerts",
        sorter: true,
        icon: alertsColumnIconMap["alerts_count"],
        stateData: state,
        key: "paid_alerts_count"
      }),
    dataIndex: "paid_alerts_count",
    width: 150
  },
  alerts_in_debt_count: {
    key: "alerts_in_debt_count",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Alerts in Debt",
        sorter: true,
        icon: alertsColumnIconMap["alerts_in_debt_count"],
        stateData: state,
        key: "alerts_in_debt_count"
      }),
    dataIndex: "alerts_in_debt_count",
    width: 160
  },
  receipt_status: {
    key: "receipt_status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Receipt status",
        sorter: true,
        icon: alertsColumnIconMap["receipt_status"],
        stateData: state,
        key: "receipt_status"
      }),
    dataIndex: "receipt_status",
    width: 170,
    render: (value) => {
      const meta = RECEIPT_STATUS_META[value]
      if (!meta) return "—"
      return (
        <Tag color="default" key={value} style={{ margin: 0 }} className={"simple large"}>
          <Space>
            {meta.outlined ? (
              <Badge
                color="transparent"
                styles={{
                  indicator: { border: `1px solid ${meta.color}`, boxSizing: "border-box" }
                }}
              />
            ) : (
              <Badge color={meta.color} />
            )}
            <Text>{meta.label}</Text>
          </Space>
        </Tag>
      )
    }
  },
  programs: {
    key: "programs",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Programs",
        sorter: true,
        icon: alertsColumnIconMap["programs"],
        stateData: state,
        key: "programs"
      }),
    dataIndex: "programs",
    width: 160,
    render: (value) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return "—"
      return (
        <Flex gap={4} wrap>
          {value.map((program) => {
            const meta = RECEIPT_STATUS_META[program?.status]
            return (
              <Tag
                color="default"
                key={program?.name}
                style={{ margin: 0 }}
                className={"simple large"}
              >
                <Space>
                  <Badge color={meta?.color || "#8C8C8C"} />
                  <Text>{PROGRAM_LABELS[program?.name] || program?.name}</Text>
                </Space>
              </Tag>
            )
          })}
        </Flex>
      )
    }
  },
  merchant_descriptor: {
    key: "merchant_descriptor",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Billing descriptor",
        sorter: true,
        icon: alertsColumnIconMap["merchant_descriptor"],
        stateData: state,
        key: "merchant_descriptor"
      }),
    dataIndex: "merchant_descriptor",
    width: 185,
    //render: (_) => _?.join(', ')
    render: (_) => (
      <Paragraph
        ellipsis={{ rows: 4, expandable: "collapsible" }}
        //copyable={!!_?.length}
      >
        {_?.join(", ")}
      </Paragraph>
    )
    //width: 185,
  },
  enrollments: {
    key: "enrollments",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Enrollments",
        sorter: true,
        icon: alertsColumnIconMap["enrollments"],
        stateData: state,
        key: "enrollments"
      }),
    minWidth: 150,
    render: (_) => (
      <Flex gap={4} wrap={true}>
        {_?.map((enrollment) => {
          return (
            <Tag
              color="default"
              key={enrollment?.name}
              style={{ margin: 0 }}
              className={"simple large"}
            >
              <Space>
                <Badge
                  status={tagMap[enrollment?.status?.toLowerCase()] || "default"}
                  color={tagColorMap[enrollment?.status]}
                />
                <Text>{serviceMap[enrollment?.name]}</Text>
              </Space>
            </Tag>
          )
        })}
      </Flex>
    ),
    dataIndex: "enrollments"
  },
  created_timestamp: {
    key: "created_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Creation date",
        sorter: true,
        icon: alertsColumnIconMap["created_timestamp"],
        stateData: state,
        key: "created_timestamp"
      }),
    dataIndex: "created_timestamp",
    width: 185,
    render: (_) => (
      <Space direction="vertical" size={4}>
        <Text>{dayjs(_).format(dateFormat)}</Text>
        <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
      </Space>
    )
  },
  user_card: {
    key: "user_card",
    title: (state) =>
      titleSortFilters({
        title: "Card",
        icon: alertsColumnIconMap["user_card"],
        stateData: state,
        key: "user_card"
      }),
    dataIndex: "user_card",
    render: (_) => {
      let { last4, brand } = _?.[0] || { brand: null, last4: null }
      return (
        <Space>
          {brand && <img src={Images[brand?.toLowerCase()]} width={20} height={20} alt={brand} />}
          {last4 && (
            <>
              <Text>••••</Text>
              <Text>{last4}</Text>
            </>
          )}
        </Space>
      )
    },
    width: 140
  },
  connected_payment_processors: {
    key: "connected_payment_processors",
    title: (state) =>
      titleSortFilters({
        title: "Processor",
        icon: alertsColumnIconMap["connected_payment_processors"],
        stateData: state,
        key: "connected_payment_processors"
      }),
    dataIndex: "connected_payment_processors",
    width: 140,
    render: (_) => (
      <Flex>
        <Avatar.Group size="small">
          {_?.map((el) => {
            let payment = el === "None" ? "manual_refunds" : el
            return (
              <Tooltip title={paymentProcessorMap[payment] || payment} key={payment}>
                <Avatar src={Images[`integration_${payment}`]} style={{ borderRadius: 8 }} />
              </Tooltip>
            )
          })}
        </Avatar.Group>
      </Flex>
    )
  },
  enable_auto_refunds: {
    key: "enable_auto_refunds",
    title: (state) =>
      titleSortFilters({
        title: "Auto-refund Ethoca&CDRN",
        icon: alertsColumnIconMap["enable_auto_refunds"],
        stateData: state,
        key: "enable_auto_refunds"
      }),
    dataIndex: "enable_auto_refunds",
    width: 150,
    render: (_) => (_ ? "Yes" : "No")
  },
  auto_refund_amount_other: {
    key: "auto_refund_amount_other",
    title: (state) =>
      titleSortFilters({
        title: "Auto-refund amount Ethoca&CDRN",
        icon: alertsColumnIconMap["auto_refund_amount_other"],
        stateData: state,
        key: "auto_refund_amount_other"
      }),
    dataIndex: "auto_refund_amount_other",
    width: 185,
    render: (_, record) => (record?.enable_auto_refunds ? "$" + formatNumber(_, 2, ",") : "—")
  },
  auto_refund_amount_rdr: {
    key: "auto_refund_amount_rdr",
    title: (state) =>
      titleSortFilters({
        title: "Auto-refund amount RDR",
        icon: alertsColumnIconMap["auto_refund_amount_rdr"],
        stateData: state,
        key: "auto_refund_amount_rdr"
      }),
    dataIndex: "auto_refund_amount_rdr",
    width: 150,
    render: (_) => "$" + formatNumber(_, 2, ",")
  },
  media_source: {
    key: "media_source",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "How did you hear about us",
        sorter: true,
        icon: alertsColumnIconMap["media_source"],
        stateData: state,
        key: "media_source"
      }),
    dataIndex: "media_source",
    width: 245
  },
  pricing_schema: {
    key: "pricing_schema",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Alert pricing",
        sorter: true,
        icon: alertsColumnIconMap["pricing_schema"],
        stateData: state,
        key: "pricing_schema"
      }),
    dataIndex: "pricing_schema",
    width: 200
  },
  lookup_pricing_schema: {
    key: "lookup_pricing_schema",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Lookup pricing",
        sorter: true,
        icon: alertsColumnIconMap["lookup_pricing_schema"],
        stateData: state,
        key: "lookup_pricing_schema"
      }),
    dataIndex: "lookup_pricing_schema",
    width: 200,
    render: (_) => _ || "—"
  },
  auto_cancel_subscriptions: {
    key: "auto_cancel_subscriptions",
    title: (state) =>
      titleSortFilters({
        title: "Auto-cancel subscriptions",
        icon: alertsColumnIconMap["auto_cancel_subscriptions"],
        stateData: state,
        key: "auto_cancel_subscriptions"
      }),
    dataIndex: "auto_cancel_subscriptions",
    render: (_) => (_ ? "Yes" : "No")
  },
  auto_cancel_amount: {
    key: "auto_cancel_amount",
    title: (state) =>
      titleSortFilters({
        title: "Auto-cancel amount",
        icon: alertsColumnIconMap["auto_cancel_amount"],
        stateData: state,
        key: "auto_cancel_amount"
      }),
    dataIndex: "auto_cancel_amount",
    width: 185,
    render: (_, record) => (record?.auto_cancel_subscriptions ? "$" + formatNumber(_, 2, ",") : "—")
  },
  balance_current: {
    key: "balance_current",
    title: (state) =>
      titleSortFilters({
        title: "Current Balance",
        icon: alertsColumnIconMap["balance_current"],
        stateData: state,
        key: "balance_current",
        tooltip: "The balance is shared across all business accounts"
      }),
    dataIndex: "balance_current",
    width: 180,
    render: (_, record) => {
      let value = _ >= 0 ? "$" + formatNumber(_, 2, ",") : "-$" + formatNumber(Math.abs(_), 2, ",")
      if (record?.user_id) {
        return (
          <ButtonComponent
            type="link"
            shape="circle"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onSelectRow(record, "user_balance")
            }}
            className="flex center"
          >
            {value}
          </ButtonComponent>
        )
      } else {
        return value
      }
    }
  },
  balance_usage: {
    key: "balance_usage",
    title: (state) =>
      titleSortFilters({
        title: "Balance usage",
        icon: alertsColumnIconMap["balance_usage"],
        stateData: state,
        key: "balance_usage"
      }),
    dataIndex: "balance_usage",
    minWidth: 150,
    render: (_) => (_ ? "Yes" : "No")
  },
  updated_bh: {
    key: "updated_bh",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Updated BH",
        icon: alertsColumnIconMap["updated_bh"],
        stateData: state,
        sorter: true,
        key: "updated_bh"
      }),
    dataIndex: "updated_bh",
    width: 170,
    render: (_, record) => (
      <Flex>
        <Avatar.Group size="small">
          {_?.map((processor) => (
            <Tooltip title={paymentProcessorMap[processor] || processor} key={processor}>
              <Avatar
                src={Images[`integration_${processor}`]}
                style={{ borderRadius: 8, cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onSelectRow(record, `updated_bh:${processor}`)
                }}
              />
            </Tooltip>
          ))}
        </Avatar.Group>
      </Flex>
    )
  },
  visits_per_month: {
    key: "visits_per_month",
    title: (state) =>
      titleSortFilters({
        title: "Visits per month",
        icon: alertsColumnIconMap["visits_per_month"],
        stateData: state,
        key: "visits_per_month"
      }),
    dataIndex: "visits_per_month",
    width: 160,
    render: (_, record) => {
      return _ || _ === 0 ? formatNumber(_, 0, ",") : "—"
    }
  },
  visits_updated: {
    key: "visits_updated",
    title: (state) =>
      titleSortFilters({
        title: "Visits updated",
        icon: alertsColumnIconMap["visits_updated"],
        stateData: state,
        key: "visits_updated"
      }),
    dataIndex: "visits_updated",
    width: 150,
    render: (_, record) => {
      if (!_) return "—"
      return (
        <Space direction="vertical" size={4}>
          <Text>{dayjs(_).utc().format(dateFormat)}</Text>
          <Text type="secondary">{dayjs(_).utc().format("h:mm A [(UTC)]")}</Text>
        </Space>
      )
    }
  }
})

export const getDefaultColumn = ({ field_id, label, t }) => ({
  key: field_id,
  sorter: true,
  title: (state) =>
    titleSortFilters({
      title: t(label),
      sorter: true,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  ellipsis: true
})

const dropDownItems = [
  {
    key: "settings",
    label: "Settings"
  },
  {
    key: "delete",
    label: "Delete"
  },
  {
    key: "comments_ba_page",
    label: "Comments"
  }
]

export const getActionColumn = ({ onSelectRow }) => {
  return {
    key: "action",
    title: "Actions",
    width: 118,
    align: "center",
    render: (_, record) => (
      <Dropdown
        destroyPopupOnHide={true}
        menu={{
          items: dropDownItems.map((item) => {
            const baseItem = {
              ...item,
              label: item.label
            }

            switch (item.key) {
              case "delete":
                return { ...baseItem, disabled: !record?.is_deletable }
              default:
                return baseItem
            }
          }),
          onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation()
            onSelectRow(record, key)
          }
        }}
        placement="bottomRight"
      >
        <Flex justify="center">
          <Icon type="three_dots" style={{ transform: "rotate(0deg)" }} />
        </Flex>
      </Dropdown>
    )
  }
}
